import { CallPushId, CallPushIdSchema, LiveIdSchema, PushDeviceName, PushDeviceNameSchema, SpaceKeySchema, UserIdSchema } from "../id"
import { z } from "zod/v4"
import { CreatedAt, UpdatedAt } from "./system"
import { DocumentSnapshotLike } from "../firestore"

export enum MobileOS {
  DUMMY = "dummy", // for testing
  ANDROID = "android",
  IOS = "ios",
}

export const MobileOSSchema = z.enum(MobileOS)

export const PushDeviceDocumentBody = z.object({
  system: MobileOSSchema.describe("Push 通知を受信するデバイスの OS です。"),
  fcm_token: z.string().optional().describe("FCM トークンです。"),
  apn_token: z.string().optional().describe("APNs VoIP Push トークンです。"),
  app: z.string().describe("Push トークンを受け取るアプリを表す文字列です。 e.g. Bundle ID (APNs)"),
  name: z.string().optional().describe("デバイスの名前です。"),
  updated_at: UpdatedAt.optional(), // TODO migrate
  created_at: CreatedAt.optional(), // TODO migrate
})

export type PushDeviceDocumentBody = z.infer<typeof PushDeviceDocumentBody>

export const PushDeviceDocument = z.intersection(
  PushDeviceDocumentBody,
  z.object({
    id: PushDeviceNameSchema,
  })
)

export type PushDeviceDocument = z.infer<typeof PushDeviceDocument>

export const AddPushDeviceRequest = z.object({
  deviceName: z.string().regex(/^[-\w]{1,255}(?:\.sandbox)?$/).describe("Push 通知設定に対応したデバイスの名前です。"),
  system: MobileOSSchema,
  app: z.string().regex(/^[-.0-9a-z]+$/),
  fcmToken: z.string().optional(),
  voipToken: z.string().regex(/^(?:[-.0-9a-z]+:)?[0-9A-Fa-f]{1,64}$/).optional(),
})

export type AddPushDeviceRequest = z.infer<typeof AddPushDeviceRequest>


export enum CallPushStatus {
  calling = "calling",
  answered = "answered",
  droppedByCaller = "dropped_by_caller",
  droppedByServer = "dropped_by_server",
  droppedByCallee = "dropped_by_callee",
  notSentDueToReceiverSettings = "not_sent_due_to_receiver_settings",
  notSentDueToDeviceNotRegistered = "not_sent_due_to_device_not_registered",
  notSentDueToBroadcasting = "not_sent_due_to_broadcasting",
}

export const UserCallPushStatusSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal(CallPushStatus.calling).describe("呼び出し中"),
  }),
  z.object({
    status: z.literal(CallPushStatus.answered).describe("呼び出しに対して応答した"),
  }),
  z.object({
    status: z.literal(CallPushStatus.droppedByCaller).describe("呼び出した側が中断した"),
    acknowledged: z.boolean().optional(),
  }),
  z.object({
    status: z.literal(CallPushStatus.droppedByServer).describe("呼び出しがタイムアウトし、サーバーサイドによってキャンセルされた"),
    acknowledged: z.boolean(),
  }),
  z.object({
    status: z.literal(CallPushStatus.droppedByCallee).describe("呼び出された側が中断した"),
  }),
  z.object({
    status: z.literal(CallPushStatus.notSentDueToReceiverSettings).describe("受信者の設定により送信されなかった"),
  }),
  z.object({
    status: z.literal(CallPushStatus.notSentDueToDeviceNotRegistered).describe("受信者のデバイスが登録されていないため送信されなかった"),
  }),
  z.object({
    status: z.literal(CallPushStatus.notSentDueToBroadcasting).describe("受信者がLIVE配信中のため送信されなかった"),
    acknowledged: z.boolean().optional(),
  }),
])

export const SpaceFriendsCallPushCallerStatusSchema = z.union([
  z.literal(CallPushStatus.calling).describe("呼び出し中"),
  z.literal(CallPushStatus.droppedByCaller).describe("呼び出した側が中断した"),
  z.literal(CallPushStatus.droppedByServer).describe("呼び出しがタイムアウトし、サーバーサイドによってキャンセルされた"),
]).describe("スペース内の友だちに対する呼び出し側のステータスです。")

export const SpaceFriendsCallPushCalleeStatusSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal(CallPushStatus.calling).describe("呼び出し中"),
  }),
  z.object({
    status: z.literal(CallPushStatus.answered).describe("呼び出しに対して応答した"),
  }),
  z.object({
    status: z.literal(CallPushStatus.droppedByServer).describe("呼び出しがタイムアウトし、サーバーサイドによってキャンセルされた"),
    acknowledged: z.boolean(),
  }),
  z.object({
    status: z.literal(CallPushStatus.droppedByCallee).describe("呼び出された側が中断した"),
  }),
  z.object({
    status: z.literal(CallPushStatus.notSentDueToReceiverSettings).describe("受信者の設定により送信されなかった"),
  }),
  z.object({
    status: z.literal(CallPushStatus.notSentDueToDeviceNotRegistered).describe("受信者のデバイスが登録されていないため送信されなかった"),
  }),
  z.object({
    status: z.literal(CallPushStatus.notSentDueToBroadcasting).describe("受信者がLIVE配信中のため送信されなかった"),
    acknowledged: z.boolean().optional(),
  }),
])

export const UserCallPushDocumentMeta = z.intersection(
  UserCallPushStatusSchema,
  z.object({
    kind: z.literal("user-call"),
    caller_user: UserIdSchema.describe("呼び出し元のユーザーです。"),
    callee_user: UserIdSchema.describe("呼び出し先のユーザーです。"),
    space: SpaceKeySchema.optional().describe("呼び出し元のスペースです。"),
    visible_caller: z.enum(["public", "hidden"]).optional().describe("呼び出し元からの表示状態"),
    visible_callee: z.enum(["public", "hidden"]).optional().describe("呼び出し先からの表示状態"),
  },
))

export type UserCallPushDocumentMeta = z.infer<typeof UserCallPushDocumentMeta>

export const SpaceFriendsCallPushCallerDocumentMeta = z.object({
  kind: z.literal("space-friends-call-caller"),
  caller_user: UserIdSchema.describe("呼び出し元のユーザーです。"),
  status: SpaceFriendsCallPushCallerStatusSchema,
  space: SpaceKeySchema.optional().describe("呼び出し元のスペースです。"),
  visible: z.enum(["public", "hidden"]).optional().describe("表示状態"),
})

export type SpaceFriendsCallPushCallerDocumentMeta = z.infer<typeof SpaceFriendsCallPushCallerDocumentMeta>

export const SpaceFriendsCallPushCalleeDocumentMeta = z.intersection(
  SpaceFriendsCallPushCalleeStatusSchema,
  z.object({
    kind: z.literal("space-friends-call-callee"),
    caller_call_id: CallPushIdSchema,
    caller_user: UserIdSchema.describe("呼び出し元のユーザーです。"),
    callee_user: UserIdSchema.describe("呼び出し先のユーザーです。"),
    space: SpaceKeySchema.optional().describe("呼び出し元のスペースです。"),
    visible: z.enum(["public", "hidden"]).optional().describe("表示状態"),
  })
)

export type SpaceFriendsCallPushCalleeDocumentMeta = z.infer<typeof SpaceFriendsCallPushCalleeDocumentMeta>

export const LiveFollowerCallPushCallerDocumentMeta = z.object({
  kind: z.literal("live-follower-call-caller"),
  caller_user: UserIdSchema.describe("呼び出し元のユーザーです。"),
  status: SpaceFriendsCallPushCallerStatusSchema,
  space: SpaceKeySchema.optional().describe("呼び出し元のスペースです。"),
  live_id: LiveIdSchema.describe("ライブのIDです。"),
  visible: z.enum(["public", "hidden"]).optional().describe("表示状態"),
})

export type LiveFollowerCallPushCallerDocumentMeta = z.infer<typeof LiveFollowerCallPushCallerDocumentMeta>

export const LiveFollowerCallPushCalleeDocumentMeta = z.intersection(
  SpaceFriendsCallPushCalleeStatusSchema,
  z.object({
    kind: z.literal("live-follower-call-callee"),
    caller_call_id: CallPushIdSchema,
    caller_user: UserIdSchema.describe("呼び出し元のユーザーです。"),
    callee_user: UserIdSchema.describe("呼び出し先のユーザーです。"),
    space: SpaceKeySchema.optional().describe("呼び出し元のスペースです。"),
    live_id: LiveIdSchema.describe("ライブのIDです。"),
    visible: z.enum(["public", "hidden"]).optional().describe("表示状態"),
  })
)

export type LiveFollowerCallPushCalleeDocumentMeta = z.infer<typeof LiveFollowerCallPushCalleeDocumentMeta>

export const CallPushDocumentMeta = z.union([
  UserCallPushDocumentMeta,
  SpaceFriendsCallPushCallerDocumentMeta,
  SpaceFriendsCallPushCalleeDocumentMeta,
  LiveFollowerCallPushCallerDocumentMeta,
  LiveFollowerCallPushCalleeDocumentMeta,
])

export type CallPushDocumentMeta = z.infer<typeof CallPushDocumentMeta>

export const CallPushDocumentBody = z.intersection(
  CallPushDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type CallPushDocumentBody = z.infer<typeof CallPushDocumentBody>

export type CallPushDocument = CallPushDocumentBody & { id: CallPushId }

export const CreateUserCallPushRequest = z.object({
  kind: z.literal("user-call"),
  spaceKey: SpaceKeySchema,
  userId: UserIdSchema,
})

export type CreateUserCallPushRequest = z.infer<typeof CreateUserCallPushRequest>

export const CreateSpaceFriendsCallPushRequest = z.object({
  kind: z.literal("space-friends-call"),
  spaceKey: SpaceKeySchema,
  userIds: z.array(UserIdSchema).optional(),
})

export type CreateSpaceFriendsCallPushRequest = z.infer<typeof CreateSpaceFriendsCallPushRequest>

export const CreateLiveFollowerCallPushRequest = z.object({
  kind: z.literal("live-follower-call"),
  spaceKey: SpaceKeySchema,
  liveId: LiveIdSchema,
})

export type CreateLiveFollowerCallPushRequest = z.infer<typeof CreateLiveFollowerCallPushRequest>

export const CreateCallPushRequest = z.union([
  CreateUserCallPushRequest,
  CreateSpaceFriendsCallPushRequest,
  CreateLiveFollowerCallPushRequest,
])

export type CreateCallPushRequest = z.infer<typeof CreateCallPushRequest>

export const UpdateCallPushRequest = z.object({
  status: z.enum(CallPushStatus).optional(),
  acknowledged: z.boolean().optional(),
  visible: z.enum(["public", "hidden"]).optional().describe("コールプッシュの表示状態"),
})

export type UpdateCallPushRequest = z.infer<typeof UpdateCallPushRequest>


export class CallPushDocuments {

  static toDocumentData(doc: CallPushDocument): object {
    return {
      ...doc,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): CallPushDocument | null {
    if (!doc) {
      return null
    }

    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: CallPushId(doc.id),
      kind: data["kind"],
      callee_user: data["callee_user"],
      caller_user: data["caller_user"],
      caller_call_id: data["caller_call_id"],
      space: data["space"],
      live_id: data["live_id"],
      status: data["status"],
      acknowledged: data["acknowledged"],
      visible_caller: data["visible_caller"],
      visible_callee: data["visible_callee"],
      visible: data["visible"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

export class PushDeviceDocuments {

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): PushDeviceDocument | null {
    if (!doc) {
      return null
    }

    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: PushDeviceName(doc.id),
      system: data["system"],
      apn_token: data["apn_token"],
      fcm_token: data["fcm_token"],
      app: data["app"],
      name: data["name"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}
